import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDataProvider, useNotify } from 'react-admin';
import { nanoid } from 'nanoid';
import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Grid,
  TextField,
  Button,
  Typography,
  Chip,
  CircularProgress,
  Box,
} from '@material-ui/core';

import useDocumentTitle from '../../hooks/useDocumentTitle';
import { computeNextSilaeId, EMPLOYEE_STATUS_VIVIER } from '../../helpers';

// Dedicated vivier creation (plan 013 B4). Light form: lastname/firstname
// required, silae_id auto-minted, employee_status forced to "Vivier salarial".
// NO contract fields. The write goes through the normal `cleaner` resource —
// never the read-only care.person_vivier view.
const VivierCreate = () => {
  useDocumentTitle('Nouveau vivier');
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const history = useHistory();

  const [silaeId, setSilaeId] = useState(null);
  const [mintError, setMintError] = useState(null);
  const [form, setForm] = useState({
    firstname: '',
    lastname: '',
    email: '',
    mobile_phone: '',
    mailing_city: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    computeNextSilaeId(dataProvider)
      .then((id) => {
        if (!cancelled) setSilaeId(id);
      })
      .catch((err) => {
        if (!cancelled) {
          setMintError(err.message || 'Erreur lors du calcul du silae_id');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [dataProvider]);

  const setField = (source, value) => {
    setForm((prev) => {
      return { ...prev, [source]: value };
    });
  };

  const canSave =
    !!silaeId && !!form.firstname.trim() && !!form.lastname.trim() && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const data = {
        id: nanoid(18),
        firstname: form.firstname.trim(),
        lastname: form.lastname.trim(),
        email: form.email.trim() || undefined,
        mobile_phone: form.mobile_phone.trim() || undefined,
        mailing_city: form.mailing_city.trim() || undefined,
        silae_id: silaeId,
        employee_status: EMPLOYEE_STATUS_VIVIER,
        function: 'Cleaner',
        account_id: '0010Y00000Ijn8cQAB',
      };
      const { data: created } = await dataProvider.create('cleaner', { data });
      notify('Vivier créé', 'info');
      // Land on the dedicated vivier show (identity + vivier panel).
      const targetId = (created && created.id) || data.id;
      history.push(`/person_vivier/${encodeURIComponent(targetId)}/show`);
    } catch (err) {
      notify(err.message || 'Échec de la création du vivier', 'warning');
      setSaving(false);
    }
  };

  return (
    <Box mt={2}>
      <Card>
        <CardHeader
          title="Nouveau vivier"
          subheader="Fiche légère — le matricule est attribué automatiquement, le statut est « Vivier salarial ». Aucune information de contrat."
          action={
            silaeId ? (
              <Box mt={2} mr={2}>
                <Chip label={`Silae ${silaeId}`} color="primary" />
              </Box>
            ) : (
              !mintError && (
                <Box mt={2} mr={2}>
                  <CircularProgress size={20} />
                </Box>
              )
            )
          }
        />
        <CardContent>
          {mintError && (
            <Typography variant="body2" color="error" gutterBottom>
              {mintError}
            </Typography>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Prénom"
                required
                fullWidth
                value={form.firstname}
                onChange={(e) => {
                  return setField('firstname', e.target.value);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Nom"
                required
                fullWidth
                value={form.lastname}
                onChange={(e) => {
                  return setField('lastname', e.target.value);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="E-mail"
                fullWidth
                value={form.email}
                onChange={(e) => {
                  return setField('email', e.target.value);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Mobile"
                fullWidth
                value={form.mobile_phone}
                onChange={(e) => {
                  return setField('mobile_phone', e.target.value);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Ville"
                fullWidth
                value={form.mailing_city}
                onChange={(e) => {
                  return setField('mailing_city', e.target.value);
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
        <CardActions>
          <Button
            onClick={() => {
              return history.goBack();
            }}
            disabled={saving}
          >
            Annuler
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleSave}
            disabled={!canSave}
            startIcon={
              saving ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            Créer le vivier
          </Button>
        </CardActions>
      </Card>
    </Box>
  );
};

export default VivierCreate;
